import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Building2,
  DoorOpen,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  Wrench,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const LOCATION_TYPES = [
  { value: 'hoofdgebouw', label: 'Hoofdgebouw' },
  { value: 'bijgebouw', label: 'Bijgebouw' },
  { value: 'kantoor', label: 'Kantoor' },
  { value: 'opslag', label: 'Opslag' },
  { value: 'extern', label: 'Externe locatie' },
];

const ROOM_TYPES = [
  { value: 'zaal', label: 'Zaal' },
  { value: 'keuken', label: 'Keuken' },
  { value: 'kantoor', label: 'Kantoor' },
  { value: 'opslag', label: 'Opslag' },
  { value: 'techniek', label: 'Techniek' },
  { value: 'kinderwerkruimte', label: 'Kinderwerkruimte' },
  { value: 'gebedsruimte', label: 'Gebedsruimte' },
  { value: 'overig', label: 'Overig' },
];

const ROOM_STATUSES = [
  { value: 'beschikbaar', label: 'Beschikbaar', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'beperkt_beschikbaar', label: 'Beperkt beschikbaar', className: 'bg-amber-100 text-amber-700' },
  { value: 'gereserveerd', label: 'Gereserveerd', className: 'bg-blue-100 text-blue-700' },
  { value: 'in_onderhoud', label: 'In onderhoud', className: 'bg-orange-100 text-orange-700' },
  { value: 'schoonmaak_nodig', label: 'Schoonmaak nodig', className: 'bg-yellow-100 text-yellow-700' },
  { value: 'buiten_gebruik', label: 'Buiten gebruik', className: 'bg-red-100 text-red-700' },
  { value: 'niet_boekbaar', label: 'Niet boekbaar', className: 'bg-slate-200 text-slate-600' },
];

const MAINTENANCE_STATUSES = [
  { value: 'nieuw', label: 'Nieuw', className: 'bg-blue-100 text-blue-700' },
  { value: 'in_behandeling', label: 'In behandeling', className: 'bg-amber-100 text-amber-700' },
  { value: 'wacht_op_materiaal', label: 'Wacht op materiaal', className: 'bg-orange-100 text-orange-700' },
  { value: 'wacht_op_externe_partij', label: 'Wacht op externe partij', className: 'bg-purple-100 text-purple-700' },
  { value: 'opgelost', label: 'Opgelost', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'geannuleerd', label: 'Geannuleerd', className: 'bg-slate-200 text-slate-600' },
];

const MAINTENANCE_PRIORITIES = [
  { value: 'laag', label: 'Laag', className: 'bg-slate-100 text-slate-600' },
  { value: 'normaal', label: 'Normaal', className: 'bg-blue-100 text-blue-700' },
  { value: 'hoog', label: 'Hoog', className: 'bg-orange-100 text-orange-700' },
  { value: 'kritiek', label: 'Kritiek', className: 'bg-red-100 text-red-700' },
];

const getLabel = (options, value) => options.find(option => option.value === value)?.label || value;
const getClassName = (options, value) => options.find(option => option.value === value)?.className || 'bg-slate-100 text-slate-700';

const EMPTY_BUILDING_FORM = {
  name: '',
  address: '',
  description: '',
  location_type: 'hoofdgebouw',
  is_active: true,
  responsible_user_id: '',
  contact_name: '',
  opening_hours: '',
  notes: '',
};

const EMPTY_ROOM_FORM = {
  name: '',
  building_id: '',
  resource_id: '',
  floor_or_zone: '',
  capacity: '',
  room_type: 'zaal',
  bookable: true,
  status: 'beschikbaar',
  default_layout: '',
  amenities: '',
  responsible_user_id: '',
  notes: '',
};

const EMPTY_MAINTENANCE_FORM = {
  title: '',
  description: '',
  building_id: '',
  room_id: '',
  priority: 'normaal',
  status: 'nieuw',
  responsible_user_id: '',
  notes: '',
};

const BuildingManagementPage = () => {
  const { user, hasCapability } = useAuth();
  const canManage = hasCapability('buildings_manage');

  const [activeTab, setActiveTab] = useState('buildings');
  const [loading, setLoading] = useState(true);
  const [buildings, setBuildings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [resources, setResources] = useState([]);

  const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [buildingForm, setBuildingForm] = useState(EMPTY_BUILDING_FORM);

  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomForm, setRoomForm] = useState(EMPTY_ROOM_FORM);

  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  const [maintenanceForm, setMaintenanceForm] = useState(EMPTY_MAINTENANCE_FORM);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [buildingsRes, roomsRes, maintenanceRes, teamRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/buildings`),
        axios.get(`${API}/rooms`),
        axios.get(`${API}/maintenance-requests`),
        axios.get(`${API}/team`).catch(() => ({ data: [] })),
        axios.get(`${API}/resources`).catch(() => ({ data: [] })),
      ]);
      setBuildings(buildingsRes.data);
      setRooms(roomsRes.data);
      setMaintenanceRequests(maintenanceRes.data);
      setTeamMembers(teamRes.data);
      setResources(resourcesRes.data.filter(r => r.type === 'room'));
    } catch (error) {
      toast.error('Kon gebouwenbeheer niet laden');
    } finally {
      setLoading(false);
    }
  };

  const buildingNameById = useMemo(() => {
    const map = {};
    buildings.forEach(b => { map[b.id] = b.name; });
    return map;
  }, [buildings]);

  const roomNameById = useMemo(() => {
    const map = {};
    rooms.forEach(r => { map[r.id] = r.name; });
    return map;
  }, [rooms]);

  const memberNameById = useMemo(() => {
    const map = {};
    teamMembers.forEach(m => { map[m.id] = m.name; });
    return map;
  }, [teamMembers]);

  const resourceNameById = useMemo(() => {
    const map = {};
    resources.forEach(r => { map[r.id] = r.name; });
    return map;
  }, [resources]);

  const linkedResourceIds = useMemo(() => new Set(rooms.filter(r => r.resource_id).map(r => r.resource_id)), [rooms]);

  const stats = useMemo(() => ({
    buildings: buildings.length,
    rooms: rooms.length,
    outOfService: rooms.filter(r => ['in_onderhoud', 'buiten_gebruik', 'schoonmaak_nodig'].includes(r.status)).length,
    openMaintenance: maintenanceRequests.filter(r => !['opgelost', 'geannuleerd'].includes(r.status)).length,
  }), [buildings, rooms, maintenanceRequests]);

  // ---------- Buildings ----------
  const resetBuildingForm = () => {
    setEditingBuilding(null);
    setBuildingForm(EMPTY_BUILDING_FORM);
  };

  const openEditBuilding = (building) => {
    setEditingBuilding(building);
    setBuildingForm({
      name: building.name || '',
      address: building.address || '',
      description: building.description || '',
      location_type: building.location_type || 'hoofdgebouw',
      is_active: building.is_active !== false,
      responsible_user_id: building.responsible_user_id || '',
      contact_name: building.contact_name || '',
      opening_hours: building.opening_hours || '',
      notes: building.notes || '',
    });
    setBuildingDialogOpen(true);
  };

  const handleBuildingSubmit = async (event) => {
    event.preventDefault();
    if (!buildingForm.name) {
      toast.error('Naam is verplicht');
      return;
    }
    try {
      const payload = {
        ...buildingForm,
        address: buildingForm.address || null,
        description: buildingForm.description || null,
        responsible_user_id: buildingForm.responsible_user_id || null,
        contact_name: buildingForm.contact_name || null,
        opening_hours: buildingForm.opening_hours || null,
        notes: buildingForm.notes || null,
      };
      if (editingBuilding) {
        await axios.put(`${API}/buildings/${editingBuilding.id}`, payload);
        toast.success('Gebouw bijgewerkt');
      } else {
        await axios.post(`${API}/buildings`, payload);
        toast.success('Gebouw toegevoegd');
      }
      setBuildingDialogOpen(false);
      resetBuildingForm();
      fetchAll();
    } catch (error) {
      toast.error('Actie mislukt');
    }
  };

  const handleDeleteBuilding = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit gebouw wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/buildings/${id}`);
      toast.success('Gebouw verwijderd');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  // ---------- Rooms ----------
  const resetRoomForm = () => {
    setEditingRoom(null);
    setRoomForm({ ...EMPTY_ROOM_FORM, building_id: buildings[0]?.id || '' });
  };

  const openEditRoom = (room) => {
    setEditingRoom(room);
    setRoomForm({
      name: room.name || '',
      building_id: room.building_id || '',
      resource_id: room.resource_id || '',
      floor_or_zone: room.floor_or_zone || '',
      capacity: room.capacity ?? '',
      room_type: room.room_type || 'zaal',
      bookable: room.bookable !== false,
      status: room.status || 'beschikbaar',
      default_layout: room.default_layout || '',
      amenities: (room.amenities || []).join(', '),
      responsible_user_id: room.responsible_user_id || '',
      notes: room.notes || '',
    });
    setRoomDialogOpen(true);
  };

  const applyResourceLink = (resourceId) => {
    if (!resourceId) {
      setRoomForm({ ...roomForm, resource_id: '' });
      return;
    }
    const resource = resources.find(r => r.id === resourceId);
    setRoomForm({
      ...roomForm,
      resource_id: resourceId,
      name: resource?.name || roomForm.name,
      capacity: resource?.capacity ?? roomForm.capacity,
    });
  };

  const handleRoomSubmit = async (event) => {
    event.preventDefault();
    if (!roomForm.name || !roomForm.building_id) {
      toast.error('Naam en gebouw zijn verplicht');
      return;
    }
    try {
      const payload = {
        ...roomForm,
        resource_id: roomForm.resource_id || null,
        capacity: roomForm.capacity === '' ? null : Number(roomForm.capacity),
        floor_or_zone: roomForm.floor_or_zone || null,
        default_layout: roomForm.default_layout || null,
        amenities: roomForm.amenities
          ? roomForm.amenities.split(',').map(a => a.trim()).filter(Boolean)
          : [],
        responsible_user_id: roomForm.responsible_user_id || null,
        notes: roomForm.notes || null,
      };
      if (editingRoom) {
        await axios.put(`${API}/rooms/${editingRoom.id}`, payload);
        toast.success('Ruimte bijgewerkt');
      } else {
        await axios.post(`${API}/rooms`, payload);
        toast.success('Ruimte toegevoegd');
      }
      setRoomDialogOpen(false);
      resetRoomForm();
      fetchAll();
    } catch (error) {
      toast.error('Actie mislukt');
    }
  };

  const handleDeleteRoom = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze ruimte wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/rooms/${id}`);
      toast.success('Ruimte verwijderd');
      fetchAll();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const quickSetRoomStatus = async (room, status) => {
    try {
      await axios.put(`${API}/rooms/${room.id}`, { status });
      toast.success('Status bijgewerkt');
      fetchAll();
    } catch (error) {
      toast.error('Status bijwerken mislukt');
    }
  };

  // ---------- Maintenance requests ----------
  const resetMaintenanceForm = () => {
    setEditingMaintenance(null);
    setMaintenanceForm({ ...EMPTY_MAINTENANCE_FORM, building_id: buildings[0]?.id || '' });
  };

  const openEditMaintenance = (request) => {
    setEditingMaintenance(request);
    setMaintenanceForm({
      title: request.title || '',
      description: request.description || '',
      building_id: request.building_id || '',
      room_id: request.room_id || '',
      priority: request.priority || 'normaal',
      status: request.status || 'nieuw',
      responsible_user_id: request.responsible_user_id || '',
      notes: request.notes || '',
    });
    setMaintenanceDialogOpen(true);
  };

  const handleMaintenanceSubmit = async (event) => {
    event.preventDefault();
    if (!maintenanceForm.title) {
      toast.error('Titel is verplicht');
      return;
    }
    try {
      const payload = {
        ...maintenanceForm,
        description: maintenanceForm.description || null,
        building_id: maintenanceForm.building_id || null,
        room_id: maintenanceForm.room_id || null,
        responsible_user_id: maintenanceForm.responsible_user_id || null,
        notes: maintenanceForm.notes || null,
      };
      if (editingMaintenance) {
        await axios.put(`${API}/maintenance-requests/${editingMaintenance.id}`, payload);
        toast.success('Melding bijgewerkt');
      } else {
        await axios.post(`${API}/maintenance-requests`, payload);
        toast.success('Melding aangemaakt');
      }
      setMaintenanceDialogOpen(false);
      resetMaintenanceForm();
      fetchAll();
    } catch (error) {
      toast.error('Actie mislukt');
    }
  };

  const handleDeleteMaintenance = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze melding wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/maintenance-requests/${id}`);
      toast.success('Melding verwijderd');
      fetchAll();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const roomsForBuilding = (buildingId) => rooms.filter(r => r.building_id === buildingId);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-56"></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(item => (
              <div key={item} className="h-24 bg-slate-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="building-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Gebouwenbeheer</h1>
            <p className="text-slate-500">Gebouwen, ruimtes en onderhoudsmeldingen</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Gebouwen</p>
                <p className="text-2xl font-bold text-slate-900">{stats.buildings}</p>
              </div>
              <Building2 className="w-8 h-8 text-teal-600" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ruimtes</p>
                <p className="text-2xl font-bold text-slate-900">{stats.rooms}</p>
              </div>
              <DoorOpen className="w-8 h-8 text-blue-600" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Niet beschikbaar</p>
                <p className="text-2xl font-bold text-slate-900">{stats.outOfService}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Open meldingen</p>
                <p className="text-2xl font-bold text-slate-900">{stats.openMaintenance}</p>
              </div>
              <Wrench className="w-8 h-8 text-red-500" />
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
            <TabsList>
              <TabsTrigger value="buildings"><Building2 className="w-4 h-4 mr-2" />Gebouwen</TabsTrigger>
              <TabsTrigger value="rooms"><DoorOpen className="w-4 h-4 mr-2" />Ruimtes</TabsTrigger>
              <TabsTrigger value="maintenance"><Wrench className="w-4 h-4 mr-2" />Meldingen</TabsTrigger>
            </TabsList>
          </div>

          {/* GEBOUWEN */}
          <TabsContent value="buildings" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button
                  onClick={() => { resetBuildingForm(); setBuildingDialogOpen(true); }}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Gebouw toevoegen
                </Button>
              </div>
            )}

            {buildings.length > 0 ? (
              <div className="grid lg:grid-cols-2 gap-4">
                {buildings.map(building => (
                  <Card key={building.id} className="border-0 shadow-sm hover:shadow-md transition-shadow min-w-0">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
                            <Building2 className="w-6 h-6" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{building.name}</h3>
                            <p className="text-sm text-slate-500 truncate">{building.address || 'Geen adres'}</p>
                            <p className="text-xs text-slate-400 mt-1">{roomsForBuilding(building.id).length} ruimte(s)</p>
                          </div>
                        </div>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditBuilding(building)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Bewerken
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteBuilding(building.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Verwijderen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mt-4">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                          {getLabel(LOCATION_TYPES, building.location_type)}
                        </span>
                        {!building.is_active && (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700">Inactief</span>
                        )}
                        {building.responsible_user_id && (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
                            {memberNameById[building.responsible_user_id] || building.contact_name || 'Verantwoordelijke toegewezen'}
                          </span>
                        )}
                      </div>

                      {building.description && (
                        <p className="mt-3 text-sm text-slate-600 line-clamp-2">{building.description}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Nog geen gebouwen</h3>
                  <p className="text-slate-500 mb-4">Leg gebouwen vast om ruimtes en meldingen aan te koppelen.</p>
                  {canManage && (
                    <Button
                      onClick={() => { resetBuildingForm(); setBuildingDialogOpen(true); }}
                      className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Eerste gebouw toevoegen
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* RUIMTES */}
          <TabsContent value="rooms" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button
                  onClick={() => { resetRoomForm(); setRoomDialogOpen(true); }}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  disabled={buildings.length === 0}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ruimte toevoegen
                </Button>
              </div>
            )}

            {buildings.length === 0 && (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-8 text-center text-slate-500">
                  Voeg eerst een gebouw toe voordat je ruimtes kunt aanmaken.
                </CardContent>
              </Card>
            )}

            {rooms.length > 0 ? (
              <div className="grid lg:grid-cols-2 gap-4">
                {rooms.map(room => (
                  <Card key={room.id} className="border-0 shadow-sm hover:shadow-md transition-shadow min-w-0">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                            <DoorOpen className="w-6 h-6" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{room.name}</h3>
                            <p className="text-sm text-slate-500 truncate">{buildingNameById[room.building_id] || 'Onbekend gebouw'}</p>
                            {room.capacity != null && (
                              <p className="text-xs text-slate-400 mt-1">Capaciteit: {room.capacity}</p>
                            )}
                          </div>
                        </div>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditRoom(room)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Bewerken
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteRoom(room.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Verwijderen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mt-4">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                          {getLabel(ROOM_TYPES, room.room_type)}
                        </span>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getClassName(ROOM_STATUSES, room.status)}`}>
                          {getLabel(ROOM_STATUSES, room.status)}
                        </span>
                        {!room.bookable && (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-200 text-slate-600">Niet boekbaar</span>
                        )}
                        {room.resource_id && (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-teal-50 text-teal-700">
                            Gekoppeld aan resource: {resourceNameById[room.resource_id] || 'onbekend'}
                          </span>
                        )}
                      </div>

                      {canManage && (
                        <div className="mt-4">
                          <Select value={room.status} onValueChange={(value) => quickSetRoomStatus(room, value)}>
                            <SelectTrigger className="rounded-xl h-9 text-sm">
                              <SelectValue placeholder="Status wijzigen" />
                            </SelectTrigger>
                            <SelectContent>
                              {ROOM_STATUSES.map(status => (
                                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {room.amenities?.length > 0 && (
                        <p className="mt-3 text-xs text-slate-500 truncate">Voorzieningen: {room.amenities.join(', ')}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              buildings.length > 0 && (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-12 text-center">
                    <DoorOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Nog geen ruimtes</h3>
                    <p className="text-slate-500 mb-4">Voeg zalen, kantoren of andere ruimtes toe aan je gebouwen.</p>
                    {canManage && (
                      <Button
                        onClick={() => { resetRoomForm(); setRoomDialogOpen(true); }}
                        className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Eerste ruimte toevoegen
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )
            )}
          </TabsContent>

          {/* MELDINGEN */}
          <TabsContent value="maintenance" className="space-y-4">
            <div className="flex justify-end">
              <Button
                onClick={() => { resetMaintenanceForm(); setMaintenanceDialogOpen(true); }}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Melding maken
              </Button>
            </div>

            {maintenanceRequests.length > 0 ? (
              <div className="grid lg:grid-cols-2 gap-4">
                {maintenanceRequests.map(request => (
                  <Card key={request.id} className="border-0 shadow-sm hover:shadow-md transition-shadow min-w-0">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                            <Wrench className="w-6 h-6" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{request.title}</h3>
                            <p className="text-sm text-slate-500 truncate">
                              {buildingNameById[request.building_id] || 'Geen gebouw'}
                              {request.room_id ? ` • ${roomNameById[request.room_id] || 'onbekende ruimte'}` : ''}
                            </p>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditMaintenance(request)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Bewerken
                            </DropdownMenuItem>
                            {canManage && (
                              <DropdownMenuItem onClick={() => handleDeleteMaintenance(request.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Verwijderen
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex flex-wrap gap-2 mt-4">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getClassName(MAINTENANCE_STATUSES, request.status)}`}>
                          {getLabel(MAINTENANCE_STATUSES, request.status)}
                        </span>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getClassName(MAINTENANCE_PRIORITIES, request.priority)}`}>
                          Prioriteit {getLabel(MAINTENANCE_PRIORITIES, request.priority).toLowerCase()}
                        </span>
                      </div>

                      {request.description && (
                        <p className="mt-3 text-sm text-slate-600 line-clamp-2">{request.description}</p>
                      )}

                      <p className="mt-3 text-xs text-slate-400">
                        Verantwoordelijke: {memberNameById[request.responsible_user_id] || 'Niet toegewezen'}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Geen meldingen</h3>
                  <p className="text-slate-500 mb-4">Meld schade, lekkages of andere onderhoudspunten.</p>
                  <Button
                    onClick={() => { resetMaintenanceForm(); setMaintenanceDialogOpen(true); }}
                    className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Eerste melding maken
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Building dialog */}
        <Dialog open={buildingDialogOpen} onOpenChange={setBuildingDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBuilding ? 'Gebouw bewerken' : 'Gebouw toevoegen'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBuildingSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="building_name">Naam *</Label>
                <Input
                  id="building_name"
                  value={buildingForm.name}
                  onChange={(e) => setBuildingForm({ ...buildingForm, name: e.target.value })}
                  placeholder="Bijv. Kerkgebouw"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="building_address">Adres</Label>
                <Input
                  id="building_address"
                  value={buildingForm.address}
                  onChange={(e) => setBuildingForm({ ...buildingForm, address: e.target.value })}
                  placeholder="Straat en huisnummer, plaats"
                  className="rounded-xl"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type locatie</Label>
                  <Select value={buildingForm.location_type} onValueChange={(value) => setBuildingForm({ ...buildingForm, location_type: value })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCATION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Verantwoordelijke</Label>
                  <Select
                    value={buildingForm.responsible_user_id || 'none'}
                    onValueChange={(value) => setBuildingForm({ ...buildingForm, responsible_user_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Geen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen</SelectItem>
                      {teamMembers.map(member => (
                        <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="building_contact">Contactpersoon (extern/vrije tekst)</Label>
                <Input
                  id="building_contact"
                  value={buildingForm.contact_name}
                  onChange={(e) => setBuildingForm({ ...buildingForm, contact_name: e.target.value })}
                  placeholder="Bijv. koster of externe beheerder"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="building_hours">Openingstijden / standaard beschikbaarheid</Label>
                <Input
                  id="building_hours"
                  value={buildingForm.opening_hours}
                  onChange={(e) => setBuildingForm({ ...buildingForm, opening_hours: e.target.value })}
                  placeholder="Bijv. ma-vr 09:00-17:00"
                  className="rounded-xl"
                />
              </div>
              <Textarea
                value={buildingForm.description}
                onChange={(e) => setBuildingForm({ ...buildingForm, description: e.target.value })}
                placeholder="Omschrijving"
                className="rounded-xl resize-none"
                rows={2}
              />
              <Textarea
                value={buildingForm.notes}
                onChange={(e) => setBuildingForm({ ...buildingForm, notes: e.target.value })}
                placeholder="Interne opmerkingen"
                className="rounded-xl resize-none"
                rows={2}
              />
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={buildingForm.is_active}
                  onChange={(e) => setBuildingForm({ ...buildingForm, is_active: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                Actief
              </label>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setBuildingDialogOpen(false)} className="rounded-xl">
                  Annuleren
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                  {editingBuilding ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Room dialog */}
        <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRoom ? 'Ruimte bewerken' : 'Ruimte toevoegen'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRoomSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="room_name">Naam *</Label>
                  <Input
                    id="room_name"
                    value={roomForm.name}
                    onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
                    placeholder="Bijv. Grote zaal"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gebouw *</Label>
                  <Select value={roomForm.building_id} onValueChange={(value) => setRoomForm({ ...roomForm, building_id: value })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Kies gebouw" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildings.map(building => (
                        <SelectItem key={building.id} value={building.id}>{building.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Koppel aan bestaande resource (optioneel)</Label>
                <Select
                  value={roomForm.resource_id || 'none'}
                  onValueChange={(value) => applyResourceLink(value === 'none' ? '' : value)}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Geen koppeling" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Geen koppeling</SelectItem>
                    {resources
                      .filter(r => r.id === roomForm.resource_id || !linkedResourceIds.has(r.id))
                      .map(resource => (
                        <SelectItem key={resource.id} value={resource.id}>{resource.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Naam en capaciteit worden overgenomen van de gekozen resource.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="room_floor">Verdieping / zone</Label>
                  <Input
                    id="room_floor"
                    value={roomForm.floor_or_zone}
                    onChange={(e) => setRoomForm({ ...roomForm, floor_or_zone: e.target.value })}
                    placeholder="Bijv. begane grond"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="room_capacity">Capaciteit</Label>
                  <Input
                    id="room_capacity"
                    type="number"
                    min="0"
                    value={roomForm.capacity}
                    onChange={(e) => setRoomForm({ ...roomForm, capacity: e.target.value })}
                    placeholder="Aantal personen"
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ruimtetype</Label>
                  <Select value={roomForm.room_type} onValueChange={(value) => setRoomForm({ ...roomForm, room_type: value })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOM_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={roomForm.status} onValueChange={(value) => setRoomForm({ ...roomForm, status: value })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOM_STATUSES.map(status => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Verantwoordelijke</Label>
                <Select
                  value={roomForm.responsible_user_id || 'none'}
                  onValueChange={(value) => setRoomForm({ ...roomForm, responsible_user_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Geen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Geen</SelectItem>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="room_layout">Standaardopstelling</Label>
                <Input
                  id="room_layout"
                  value={roomForm.default_layout}
                  onChange={(e) => setRoomForm({ ...roomForm, default_layout: e.target.value })}
                  placeholder="Bijv. theateropstelling"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="room_amenities">Voorzieningen (kommagescheiden)</Label>
                <Input
                  id="room_amenities"
                  value={roomForm.amenities}
                  onChange={(e) => setRoomForm({ ...roomForm, amenities: e.target.value })}
                  placeholder="Bijv. beamer, geluidsinstallatie, whiteboard"
                  className="rounded-xl"
                />
              </div>
              <Textarea
                value={roomForm.notes}
                onChange={(e) => setRoomForm({ ...roomForm, notes: e.target.value })}
                placeholder="Interne opmerkingen"
                className="rounded-xl resize-none"
                rows={2}
              />
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={roomForm.bookable}
                  onChange={(e) => setRoomForm({ ...roomForm, bookable: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                Beschikbaar voor reserveringen
              </label>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setRoomDialogOpen(false)} className="rounded-xl">
                  Annuleren
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                  {editingRoom ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Maintenance dialog */}
        <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingMaintenance ? 'Melding bewerken' : 'Melding maken'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleMaintenanceSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="maintenance_title">Titel *</Label>
                <Input
                  id="maintenance_title"
                  value={maintenanceForm.title}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, title: e.target.value })}
                  placeholder="Bijv. Lekkage plafond"
                  className="rounded-xl"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gebouw</Label>
                  <Select
                    value={maintenanceForm.building_id || 'none'}
                    onValueChange={(value) => setMaintenanceForm({ ...maintenanceForm, building_id: value === 'none' ? '' : value, room_id: '' })}
                    disabled={!canManage && Boolean(editingMaintenance)}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Kies gebouw" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen</SelectItem>
                      {buildings.map(building => (
                        <SelectItem key={building.id} value={building.id}>{building.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ruimte</Label>
                  <Select
                    value={maintenanceForm.room_id || 'none'}
                    onValueChange={(value) => setMaintenanceForm({ ...maintenanceForm, room_id: value === 'none' ? '' : value })}
                    disabled={!canManage && Boolean(editingMaintenance)}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Kies ruimte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen</SelectItem>
                      {roomsForBuilding(maintenanceForm.building_id).map(room => (
                        <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prioriteit</Label>
                  <Select
                    value={maintenanceForm.priority}
                    onValueChange={(value) => setMaintenanceForm({ ...maintenanceForm, priority: value })}
                    disabled={!canManage && Boolean(editingMaintenance)}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MAINTENANCE_PRIORITIES.map(priority => (
                        <SelectItem key={priority.value} value={priority.value}>{priority.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {editingMaintenance && (
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={maintenanceForm.status} onValueChange={(value) => setMaintenanceForm({ ...maintenanceForm, status: value })}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MAINTENANCE_STATUSES.map(status => (
                          <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {canManage && (
                <div className="space-y-2">
                  <Label>Verantwoordelijke</Label>
                  <Select
                    value={maintenanceForm.responsible_user_id || 'none'}
                    onValueChange={(value) => setMaintenanceForm({ ...maintenanceForm, responsible_user_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Geen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen</SelectItem>
                      {teamMembers.map(member => (
                        <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Textarea
                value={maintenanceForm.description}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })}
                placeholder="Omschrijving van het probleem"
                className="rounded-xl resize-none"
                rows={3}
              />
              <Textarea
                value={maintenanceForm.notes}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, notes: e.target.value })}
                placeholder="Interne opmerkingen"
                className="rounded-xl resize-none"
                rows={2}
              />
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setMaintenanceDialogOpen(false)} className="rounded-xl">
                  Annuleren
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                  {editingMaintenance ? 'Opslaan' : 'Melden'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BuildingManagementPage;
